﻿import { AxiosInstance } from "axios";
import { isCancelled } from "helpers";
import { createLogger } from "logging";
import { AddBoardModel } from "models/AddBoardModel";
import { BoardListModel } from "models/BoardListModel";
import { BoardModel } from "models/BoardModel";
import { BoardRunsModel } from "models/BoardRunsModel";
import { BoardSettingsModel } from "models/BoardSettingsModel";
import { BoardUserRoleModel } from "models/BoardUserRoleModel";
import { EntityActionResult } from "models/EntityActionResult";
import { PatchBoardConfigurationModel } from "models/PatchBoardConfigurationModel";
import { PatchBoardModel } from "models/PatchBoardModel";

import { DownloadApi } from "./DownloadApi";

const logger = createLogger("BoardApi");

export class BoardApi {
  constructor(
    private axios: AxiosInstance,
    private downloadApi: DownloadApi
  ) {}

  async getBoards(type: string, signal?: AbortSignal): Promise<BoardListModel[]> {
    try {
      const response = await this.axios.get<BoardListModel[]>(`/boards/${type}`, { signal });
      return response.data;
    } catch (error) {
      if (!isCancelled(error)) {
        logger.error("Failed to fetch boards", error);
      }
      throw error;
    }
  }

  async getBoard(boardId: string | undefined, signal?: AbortSignal): Promise<BoardModel | undefined> {
    if (!boardId) throw new Error("No boardId provided");

    try {
      const response = await this.axios.get<BoardModel>(`/boards/${boardId}`, { signal });
      return response.data;
    } catch (error) {
      if (!isCancelled(error)) {
        logger.error("Failed to fetch board", error);
      }
      throw error;
    }
  }

  async getBoardSettings(boardId: string | undefined, signal?: AbortSignal): Promise<BoardSettingsModel | undefined> {
    if (!boardId) throw new Error("No boardId provided");

    try {
      const response = await this.axios.get<BoardSettingsModel>(`/boards/${boardId}/settings`, { signal });
      return response.data;
    } catch (error) {
      if (!isCancelled(error)) {
        logger.error("Failed to fetch board settings", error);
      }
      throw error;
    }
  }

  async getBoardRuns(boardId: string | undefined, signal?: AbortSignal): Promise<BoardRunsModel | undefined> {
    if (!boardId) throw new Error("No boardId provided");

    try {
      const response = await this.axios.get<BoardRunsModel>(`/boards/${boardId}/runs`, { signal });
      return response.data;
    } catch (error) {
      if (!isCancelled(error)) {
        logger.error("Failed to fetch board runs", error);
      }
      throw error;
    }
  }

  async getBoardUsers(boardId: string | undefined, signal?: AbortSignal): Promise<BoardUserRoleModel[]> {
    if (!boardId) throw new Error("No boardId provided");

    try {
      const response = await this.axios.get<BoardUserRoleModel[]>(`/boards/${boardId}/users`, { signal });
      return response.data;
    } catch (error) {
      if (!isCancelled(error)) {
        logger.error("Failed to fetch board users", error);
      }
      throw error;
    }
  }

  async createBoard(model: AddBoardModel): Promise<EntityActionResult> {
    try {
      const response = await this.axios.post<EntityActionResult>(`/boards`, model);
      return response.data;
    } catch (error) {
      logger.error("Failed to create board", error);
      throw error;
    }
  }

  async updateBoard(model: PatchBoardModel): Promise<EntityActionResult> {
    try {
      const response = await this.axios.put<EntityActionResult>(`/boards/${model.id}`, model);
      return response.data;
    } catch (error) {
      logger.error("Failed to update board", error);
      throw error;
    }
  }

  async updateBoardConfiguration(model: PatchBoardConfigurationModel): Promise<EntityActionResult> {
    try {
      const response = await this.axios.put<EntityActionResult>(`/boards/${model.id}/configuration`, model, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      logger.error("Failed to update board configuration", error);
      throw error;
    }
  }

  async deleteBoard(boardId: string | undefined): Promise<EntityActionResult> {
    if (!boardId) throw new Error("No boardId provided");

    try {
      const response = await this.axios.delete<EntityActionResult>(`/boards/${boardId}`);
      return response.data;
    } catch (error) {
      logger.error("Failed to delete board", error);
      throw error;
    }
  }

  async deleteEmailTemplate(boardId: string | undefined): Promise<EntityActionResult> {
    if (!boardId) throw new Error("No boardId provided");
    try {
      const response = await this.axios.delete<EntityActionResult>(`/boards/${boardId}/emailTemplate`);
      return response.data;
    } catch (error) {
      logger.error("Failed to delete email template", error);
      throw error;
    }
  }

  async deleteReportTemplate(boardId: string | undefined): Promise<EntityActionResult> {
    if (!boardId) throw new Error("No boardId provided");

    try {
      const response = await this.axios.delete<EntityActionResult>(`/boards/${boardId}/reportTemplate`);
      return response.data;
    } catch (error) {
      logger.error("Failed to delete report template", error);
      throw error;
    }
  }

  async getBoardConfigZip(boardId: string | undefined): Promise<void> {
    if (!boardId) throw new Error("No boardId provided");
    await this.downloadApi.handleDownload(`/boards/${boardId}/configurationZip`, boardId);
  }

  async createExcelForAllBoards(): Promise<void> {
    await this.downloadApi.handleDownload(`/boards/adminexcelforallboards`, "AllBoardsExcelExport.xlsx");
  }
}
